/**
 * Section displaying review feedback items sorted by severity.
 */
import type { FC } from "react";

import type { ExecutionFlowNode } from "../../../types";

import { getNormalizedFilepath } from "../../../utils/executionNodeHelpers";
import { Badge } from "../../common/Badge";
import { ChatIcon } from "../../common/icons";
import { SectionHeader } from "../../common/SectionHeader";
import { Text } from "../../common/Text";

type ReviewFeedbackItem = NonNullable<
  ExecutionFlowNode["review_feedback"]
>[number];

interface ReviewFeedbackSectionProps {
  feedback: ReviewFeedbackItem[];
}

const getSeverityVariant = (severity: string) => {
  switch (severity.toLowerCase()) {
    case "high": {
      return "danger";
    }
    case "medium": {
      return "warning";
    }
    case "low": {
      return "info";
    }
    default: {
      return "default";
    }
  }
};

const getSeverityWeight = (severity: string) => {
  switch (severity.toLowerCase()) {
    case "high": {
      return 3;
    }
    case "medium": {
      return 2;
    }
    case "low": {
      return 1;
    }
    default: {
      return 0;
    }
  }
};

export const ReviewFeedbackSection: FC<ReviewFeedbackSectionProps> = ({
  feedback,
}) => {
  const sortedFeedback = [...feedback].toSorted(
    (a, b) => getSeverityWeight(b.severity) - getSeverityWeight(a.severity),
  );

  return (
    <div>
      <SectionHeader title="Review Feedback" icon={<ChatIcon size={16} />} />
      <div className="overflow-hidden rounded-none border border-slate-300 bg-white shadow-[4px_4px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-950/50 dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)]">
        <div className="divide-y divide-gray-200 dark:divide-white/10">
          {sortedFeedback.map((item, idx) => {
            const normalizedFilepath = getNormalizedFilepath(item);
            return (
              <div key={idx} className="flex items-start gap-4 p-4">
                <Badge
                  variant={getSeverityVariant(item.severity)}
                  size="sm"
                  className="flex-shrink-0 capitalize"
                >
                  {item.severity}
                </Badge>
                <div
                  className={normalizedFilepath ? "flex-1 space-y-2" : "flex-1"}
                >
                  {normalizedFilepath && (
                    <Text
                      variant="mono"
                      className="inline-flex items-center border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] dark:border-slate-700 dark:bg-slate-900/80"
                    >
                      {normalizedFilepath}
                    </Text>
                  )}
                  <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
                    {item.message}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
